/**
 * Unit tripwires for assistant-authored scenes.
 *
 * Shared by every authoring path (direct tools and the build_spatial DSL) so
 * the threshold and the advice can't drift apart between them.
 */

/**
 * Above this, a fontSize is almost certainly a web/du number rather than a
 * scene one. A 1 m em-square is 100, so 48–110 — the range CSS and design
 * tools train callers to reach for — yields metre-tall glyphs.
 */
export const FONT_SIZE_SUSPECT_ABOVE = 40;

/**
 * Warning for a suspect fontSize, or null when it's in range. Non-numeric
 * input is not a unit mistake (it's a property binding or a schema error) and
 * returns null.
 */
export function fontSizeUnitWarning(fontSize: unknown, label?: string): string | null {
    if (typeof fontSize !== 'number' || !(fontSize > FONT_SIZE_SUSPECT_ABOVE)) return null;
    const where = label ? ` on "${label}"` : '';
    return `WARNING: fontSize ${fontSize}${where} is a ${(fontSize / 100).toFixed(1)}m em-square — likely a unit mistake. `
        + `Template-scale text is ~du/10 (e.g. a 96du headline → fontSize 9.6).`;
}

// ── Bevel corner radius (#759) ──────────────────────────
// The field used to be `radius`, a 0..1 FRACTION of the slab's smaller side;
// it is now `cornerRadius` in METRES, with `pill` for "a capsule at whatever
// size the layout picks".
//
// The viewer still migrates the old field on load, so a stale build renders —
// but it is authoring against a vocabulary that no longer exists, and the
// fraction reading is the wrong one to keep: every DSL example ever written
// passed a metre-looking value (`radius: 0.012` for a 12 mm card corner),
// which as a fraction is a nearly sharp edge. So a legacy `radius` is read as
// METRES, the number the caller almost certainly meant, and reported.

/**
 * A pre-#759 `radius` read as metres, or null when it carries no usable radius
 * — non-numeric, non-finite, or <= 0. That is the same reading the viewer's own
 * `_migrateLegacyRadius` takes, and callers should treat null as "leave it
 * sharp" rather than as "use a default".
 *
 * An old fractional 0.5 ("pill") survives this as 0.5 m rather than being
 * guessed back into `pill: true`. It does not need guessing: Bevel clamps the
 * radius to half the smaller side at render, so an oversized value degrades to
 * a capsule — the shape the author asked for — without this having to infer
 * intent from magnitude.
 */
export function legacyBevelRadiusMetres(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/** Warning for a build that still authors the pre-#759 `radius`. */
export function bevelRadiusUnitWarning(label?: string): string {
    const where = label ? ` on "${label}"` : '';
    return `WARNING: \`radius\`${where} is the pre-#759 Bevel field. Read as \`cornerRadius\` in METRES. `
        + 'Use cornerRadius (metres, e.g. 0.012 for a 12mm corner), or pill: true for a capsule.';
}
